import { Text } from '../Text';
import { ExternalLink } from '../ExternalLink';
import { CustomLink } from '../CustomLink';
import { CodeArea } from '../CodeArea';
import { IntersectionSubscriber } from '../../SectionIntersectionDetection/IntersectionSubscriber';
import React from 'react';
import { getHeadingId } from './utils';
import { Heading } from '../Heading';
import style from './ReactMarkdownDisplay.scss';
import Link from 'wix-ui-icons-common/Link';
import { ReactMarkdownDisplay } from '.';
import { NAV_TYPES } from '../../../LoggerFactory/BiAnalyticsLogger/events/consts';
import { logNavigationClickFromLink } from '../../../LoggerFactory/BiAnalyticsLogger/events';
import { isAbsoluteUrl, isMailUrl } from '../../../../utils/locationUtils';
import { TableHeading } from '../TableHeading';

export const CustomHeading =
  ({ markdownPath, textLight, dataHooks }) =>
  ({ children, level, value: nodeId }) => {
    const isEligibleToBeLinked = (_level) => _level <= 6;
    const isLinked = isEligibleToBeLinked(level);
    const id = isLinked
      ? getHeadingId({ id: nodeId, level, children }, markdownPath)
      : undefined;

    return (
      <Heading
        appearance={`H${level}`}
        dataHook={dataHooks.h}
        className={style.heading}
        light={textLight}
        id={id}
      >
        {isLinked && (
          <CustomLink
            className={style.customLink}
            to={{
              pathname: `/${markdownPath}`,
              hash: id,
            }}
          >
            <Link size="20px" />
          </CustomLink>
        )}
        {children}
      </Heading>
    );
  };

export const CustomTableDataRenderer = ({ children }) => {
  const shouldReplaceChildren = children.every(
    (child) => typeof child === 'string',
  );
  if (shouldReplaceChildren) {
    const markdown = children
      .map((child) => child)
      .join()
      .trim();

    return <ReactMarkdownDisplay markdown={markdown} textSize="small" />;
  }
  return children;
};

export const CustomLinkRenderer =
  ({ textSize, textWeight, textLight, dataHooks, logger, store }) =>
  ({ href = '', title, children }) => {
    const onLinkClick = () =>
      logNavigationClickFromLink(logger, href, store, NAV_TYPES.CONTENT_LINK);
    if (isAbsoluteUrl(href) || isMailUrl(href)) {
      return (
        <ExternalLink
          to={href}
          dataHook={dataHooks.a}
          size={textSize}
          light={textLight}
          onClick={onLinkClick}
          title={title}
        >
          {children}
        </ExternalLink>
      );
    } else {
      return (
        <CustomLink
          to={href.toLowerCase()}
          title={title}
          data-hook={dataHooks.a}
          onClick={onLinkClick}
        >
          {children}
        </CustomLink>
      );
    }
  };

export const defaultRenderers = (dataHooks = {}, _props = {}) => {
  const textSize = _props.textSize || 'medium';
  const textLight = !!_props.textLight;
  const textWeight = _props.textWeight || 'thin';
  const { markdownPath, logger, store } = _props;
  return {
    paragraph: function customText(props) {
      return (
        props.children && (
          <Text
            dataHook={dataHooks.p}
            size={textSize}
            tagName="p"
            light={textLight}
            weight={textWeight}
          >
            {props.children}
          </Text>
        )
      );
    },
    heading: CustomHeading({
      markdownPath,
      textLight,
      dataHooks,
    }),
    link: CustomLinkRenderer({
      textSize,
      textWeight,
      textLight,
      logger,
      store,
      dataHooks,
    }),
    linkReference: CustomLinkRenderer({
      textSize,
      textWeight,
      textLight,
      logger,
      store,
      dataHooks,
    }),
    listItem: function customListItem(props) {
      return (
        <li>
          <Text
            dataHook={dataHooks.span}
            tagName="span"
            size={textSize}
            light={textLight}
            weight={textWeight}
          >
            {props.children}
          </Text>
        </li>
      );
    },
    code: function customCode(props) {
      return <CodeArea {...props} />;
    },
    strong: (props) => (
      <Text
        weight="bold"
        tagName="span"
        inline
        size={textSize}
        light={textLight}
      >
        {props.children}
      </Text>
    ),
    section: ({ children, id, data }) => {
      return (
        <IntersectionSubscriber
          id={`${markdownPath}#${id}`}
          data-markdown-path={data.markdownPath}
        >
          {children}
        </IntersectionSubscriber>
      );
    },
    tableCell: (props) => {
      if (props.isHeader) {
        return <TableHeading tagName="th">{props.children}</TableHeading>;
      }
      return (
        <Text size="small" tagName="td">
          {props.children}
        </Text>
      );
    },
  };
};
